import React, { useState, useEffect, useRef } from 'react'

interface TooltipProps {
    label: React.ReactNode
    children: React.ReactNode
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'topright' | 'bottomright' | 'bottomleft'
}

const Tooltip: React.FC<TooltipProps> = ({ label, children, placement = 'top' }) => {
    const tooltipRef = useRef<HTMLSpanElement>(null)
    const [finalPlacement, setFinalPlacement] = useState(placement)

    useEffect(() => {
        const checkPosition = () => {
            if (tooltipRef.current) {
                const rect = tooltipRef.current.getBoundingClientRect()
                const { innerWidth, innerHeight } = window

                let adjustedPlacement = placement

                if (placement === 'top' && rect.top < 0) adjustedPlacement = 'bottom'
                if (placement === 'bottom' && rect.bottom > innerHeight) adjustedPlacement = 'top'
                if (placement === 'left' && rect.left < 0) adjustedPlacement = 'right'
                if (placement === 'right' && rect.right > innerWidth) adjustedPlacement = 'left'
                if (placement === 'topright' && rect.top < 0) adjustedPlacement = 'bottomright'
                if (placement === 'bottomright' && rect.bottom > innerHeight) adjustedPlacement = 'topright'

                setFinalPlacement(adjustedPlacement)
            }
        }

        checkPosition()
        window.addEventListener('scroll', checkPosition)
        return () => window.removeEventListener('scroll', checkPosition)
    }, [placement])

    const positionClasses = {
        top: 'bottom-full mb-2 left-1/2 transform -translate-x-1/2',
        bottom: 'top-full mt-2 left-1/2 transform -translate-x-1/2',
        left: 'right-full mr-2 top-1/2 transform -translate-y-1/2',
        right: 'left-full ml-2 top-1/2 transform -translate-y-1/2',
        topright: 'bottom-full mb-2 left-2',
        bottomright: 'top-full mt-2 left-2',
        bottomleft: 'top-full mt-2 right-2'
    }

    const arrowClasses = {
        top: 'border-t-4 border-t-gray-700 top-full left-1/2 transform -translate-x-1/2',
        bottom: 'border-b-4 border-b-gray-700 -top-1 left-1/2 transform -translate-x-1/2',
        left: 'border-l-4 border-l-gray-700 right-0 top-1/2 transform -translate-y-1/2',
        right: 'border-r-4 border-r-gray-700 left-0 top-1/2 transform -translate-y-1/2',
        topright: 'border-t-4 border-t-gray-700 top-full left-2',
        bottomright: 'border-b-4 border-b-gray-700 -top-1 left-2',
        bottomleft: 'border-b-4 border-b-gray-700 -top-1 right-2'
    }

    return (
        <div>
            <span className='relative inline-block group w-full'>
                {children}

                <span
                    ref={tooltipRef}
                    className={`absolute z-50 px-3 py-2 text-xs text-white bg-gray-700 dark:bg-gray-100 dark:text-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none
                ${positionClasses[finalPlacement]}`}
                >
                    {label}

                    <span
                        className={`absolute w-0 h-0 border-l-4 border-r-4 border-transparent ${arrowClasses[finalPlacement]}`}
                    />
                </span>
            </span>
        </div>
    )
}

export default Tooltip
